import React from "react";

type Variant = "primary" | "secondary" | "tertiary" | "fibi";
type Size = "small" | "medium" | "large";
type IconHoverStyle = "rotate" | "scale" | "none";

interface ButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  /** controls icon animation on hover */
  iconHoverStyle?: IconHoverStyle; // "rotate" | "scale" | "none"
}

const Button: React.FC<ButtonProps> = ({
  label,
  onClick,
  disabled,
  variant = "primary",
  size = "medium",
  fullWidth,
  startIcon,
  endIcon,
  iconHoverStyle = "rotate",
}) => {
  const hasStart = !!startIcon;
  const hasEnd = !!endIcon;

  // Background should originate from icon side.
  // If both icons exist, originate from left.
  const bgFromRight = !hasStart && hasEnd;

  // Icon hover animation
  const iconAnim =
    iconHoverStyle === "scale"
      ? "group-hover:scale-125"
      : iconHoverStyle === "rotate"
        ? "group-hover:-rotate-45"
        : "";

  const sizeClasses =
    size === "small" ? "text-sm" : size === "large" ? "text-base" : "text-sm";

  const baseButtonClasses = `relative group inline-flex items-center justify-center p-1 rounded-xl transition-colors duration-200 ${sizeClasses}${
    fullWidth ? "flex-1 w-full" : ""
  }`;

  const VARIANT: Record<
    Variant,
    {
      button: string;
      overlay: string;
      label: string;
      iconBox: string; // bg + text for icon containers
      labelPad: string;
    }
  > = {
    primary: {
      button: "bg-[#0076e9]",
      overlay: "bg-white",
      label: "text-white group-hover:text-[#0076e9]",
      iconBox: "bg-white text-[#0076e9]",
      labelPad: "px-4",
    },
    secondary: {
      button: "bg-[#00C1A3]",
      overlay: "bg-[#1F8A79]",
      label: "text-white group-hover:text-black-31",
      iconBox: "bg-[#1F8A79] text-white",
      labelPad: "px-4",
    },
    tertiary: {
      button: "bg-gray-100",
      overlay: "bg-blue-200",
      label: "text-gray-800",
      iconBox: "bg-blue-200 text-gray-800",
      labelPad: "px-4",
    },
    fibi: {
      button: "bg-[#EAC5FB]",
      overlay: "bg-[#A881DB]",
      label: "text-gray-800 group-hover:text-white",
      iconBox: "bg-[#A881DB] text-white",
      labelPad: "px-4",
    },
  };

  const v = VARIANT[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseButtonClasses} ${v.button}`}
    >
      <div className="relative flex-1 flex items-center justify-between gap-2 rounded-lg overflow-hidden">
        {/* animated background, grows from icon side */}
        <div
          className={`absolute inset-y-0 ${
            bgFromRight ? "right-0 left-auto" : "left-0 right-auto"
          } w-0 group-hover:w-full ${
            v.overlay
          } rounded-lg z-0 transition-all duration-300 ease-in-out`}
        />

        {/* start icon */}
        {hasStart && (
          <span
            className={`${v.iconBox} p-3 rounded-lg z-10 transition-transform duration-300 ease-in-out ${iconAnim}`}
          >
            {startIcon}
          </span>
        )}

        {/* label */}
        <span className={`${v.label} ${v.labelPad} z-10`}>{label}</span>

        {/* end icon */}
        {hasEnd && (
          <span
            className={`${v.iconBox} p-3 rounded-lg z-10 transition-transform duration-300 ease-in-out ${iconAnim}`}
          >
            {endIcon}
          </span>
        )}
      </div>
    </button>
  );
};

export default Button;
